import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { OutlinedInput } from '@mui/material';
import { handleOnTextChange } from './handleOnTextChange';
import { handleOnBlur } from './handleOnBlur';
import { incomingValueRuleSet } from './Validation/rules/ruleSets/incomingValueRuleSet';
import { textInputStyleBuilder } from './TextInputStyles';
import { unmaskValue } from './utils/unmaskValue';
import { Adornment } from './adornment/Adornment';
import { TextInputReadOnly } from './TextInputReadOnly';
import { incomingValueMultilineRuleSet } from './Validation/rules/ruleSets/incomingValueMultilineRuleSet';
import { interstateInputEventBuilder } from '../InterstateEvents';
import { useInterstateTheme } from '../InterstateThemeProvider';
import { Box } from '../Box';
import { clsx } from 'clsx';
import { mergeSxStyle } from '../common/mergeSxStyle';
import { isFieldRequired, useValidateMinMaxProperties } from '../Validation';
import { isValueFalsey } from '../internal';
import { useValidator } from '../Validation/useValidator';
import { InputLayout } from '../InputLayout/InputLayout';
import { MaskedTextInput } from './MaskedTextInput';
import { maskValue } from './utils/maskValue';
import { jsx as _jsx } from "react/jsx-runtime";
export const TextInputBase = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    allowDecimal,
    autoComplete = 'off',
    autoFocus,
    className,
    customErrorMessages,
    decimalMaxLength,
    decimalMinLength,
    disabled,
    displayLabel = true,
    hasError,
    errorMessage,
    getError,
    helpMessage,
    successMessage,
    isSuccessful,
    type,
    id,
    inputPrefix,
    inputSuffix,
    label,
    layout,
    masking,
    maxLength,
    maxRows,
    maxValue,
    minLength,
    minRows,
    minValue,
    multiline,
    name,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    placeholder,
    readOnly,
    required,
    rows,
    size = 'medium',
    sx,
    value,
    clearButton,
    textAlign = 'left',
    ...rest
  } = props;
  const theme = useInterstateTheme();
  const {
    validationState,
    validator
  } = useValidator(Boolean(hasError), errorMessage, Boolean(isSuccessful), successMessage, customErrorMessages, helpMessage, Boolean(isFieldRequired(required)), label);
  const strValue = unmaskValue(typeof value === 'number' ? value.toString() : value, masking);

  // the currentValue is initialized in a useEffect that you can find below
  const [currentValue, setCurrentValue] = useState(strValue || '');
  useEffect(() => {}, [helpMessage, hasError, errorMessage, customErrorMessages]);
  const validationParameters = {
    allowDecimal,
    decimalMaxLength,
    decimalMinLength,
    getError,
    masking,
    maxLength,
    maxValue,
    minLength,
    minValue,
    name,
    multiline
  };
  // Update the current value with the incoming value
  // If disabled or readOnly then don't display error messages
  // If the value is a number then it is converted to a string here
  // For consistency the value should be handled as a string throughout the TextInput component
  useEffect(() => {
    const strValue = unmaskValue(typeof value === 'number' ? value.toString() : value, masking);
    if (disabled || readOnly) {
      validator.reset();
    } else {
      validator.validate(strValue, validationParameters, multiline ? incomingValueMultilineRuleSet : incomingValueRuleSet, true);
    }
    setCurrentValue(strValue ?? '');
  }, [value, disabled, readOnly, hasError, errorMessage, isSuccessful, successMessage, required]);
  useEffect(() => {
    if (!isValueFalsey(value)) {
      const strValue = unmaskValue(typeof value === 'number' ? value.toString() : value, masking);
      setCurrentValue(strValue ?? '');
    }
  }, [value]);
  const textInputStyle = useMemo(() => textInputStyleBuilder(theme, validationState?.type, Boolean(readOnly), Boolean(disabled), multiline, size, textAlign), [theme, validationState, readOnly, disabled, multiline, size]);
  useValidateMinMaxProperties(maxLength, minLength, maxValue, minValue, decimalMinLength, decimalMaxLength);
  const onClear = () => {
    setCurrentValue('');
    if (onChange) {
      const event = interstateInputEventBuilder(name, '', true, undefined, undefined, validationState.messageKey, undefined);
      onChange(event);
    }
  };
  useImperativeHandle(ref, () => ({
    clear: () => {
      onClear();
    }
  }));
  const handleOnKeyDown = event => {
    if (onKeyDown) {
      onKeyDown(event);
    }
  };
  const handleOnFocus = event => {
    if (onFocus) {
      onFocus(event);
    }
    validator.reset();
  };

  /**
   * These props are passed to the input element
   */
  const defaultValueMasked = maskValue(currentValue, masking);
  const inputProps = {
    masking,
    'data-testid': props['data-testid'],
    id,
    name,
    defaultValue: defaultValueMasked === '' ? undefined : defaultValueMasked
  };
  const rootStyle = mergeSxStyle({
    width: '100%'
  }, sx);
  return /*#__PURE__*/_jsx(Box, {
    className: clsx(['ids-text-input-container', className]),
    "aria-disabled": disabled ? 'true' : 'false',
    "data-disabled": disabled ? 'true' : 'false',
    id: `${id || 'interstate-text-input'}-root`,
    sx: rootStyle,
    children: /*#__PURE__*/_jsx(InputLayout, {
      layout: layout || 'vertical',
      disabled: Boolean(disabled),
      readOnly: Boolean(readOnly),
      required: required,
      size: size,
      displayLabel: displayLabel,
      label: label,
      validationState: validationState,
      labelId: id || 'interstate-text-input',
      children: readOnly ? /*#__PURE__*/_jsx(TextInputReadOnly, {
        inputSuffix: inputSuffix,
        inputPrefix: inputPrefix,
        value: currentValue,
        size: size,
        sx: sx,
        className: className,
        id: id,
        "data-testid": rest['data-testid']
      }) : /*#__PURE__*/_jsx(OutlinedInput, {
        autoComplete: autoComplete,
        autoFocus: autoFocus,
        inputComponent: masking ? MaskedTextInput : undefined,
        disabled: disabled,
        id: id || 'interstate-text-input',
        placeholder: placeholder,
        multiline: multiline,
        rows: rows,
        minRows: minRows,
        maxRows: maxRows,
        value: currentValue,
        onBlur: handleOnBlur(validator, validationParameters, readOnly, disabled, onBlur, onChange),
        onChange: handleOnTextChange(setCurrentValue, onChange, validator, validationParameters),
        onKeyDown: handleOnKeyDown,
        onFocus: handleOnFocus,
        sx: textInputStyle,
        endAdornment: /*#__PURE__*/_jsx(Adornment, {
          adornmentType: 'end',
          adornment: inputSuffix,
          clearButton: clearButton,
          disabled: Boolean(disabled),
          size: size,
          onClearButton: onClear,
          value: currentValue
        }),
        startAdornment: /*#__PURE__*/_jsx(Adornment, {
          adornmentType: 'start',
          adornment: inputPrefix,
          size: size,
          disabled: Boolean(disabled)
        }),
        inputProps: inputProps,
        size: 'medium',
        type: type
      })
    })
  });
});
TextInputBase.displayName = 'TextInputBase';